/**
 * The Account switcher, in the sidebar rail (Feature: user-and-data-management, Requirement 5.8).
 *
 * A control and nothing else. Three states, deliberately:
 *
 *  - **One Account** — nothing at all. Most Users are in exactly one Account, so there is nothing to
 *    switch between; a dropdown with a single option is an invitation to wonder what is missing, and the
 *    Account ID on its own is not context anybody navigates by. It is still said, once, in the user panel
 *    ({@link import('./UserPanel').UserPanel}) alongside the role — which is where a reader looks for
 *    "who am I and where am I", rather than in the rail they navigate with.
 *  - **Several** — a `<select>`. A native control rather than a custom menu because it is keyboard- and
 *    screen-reader-accessible for free, and this is a rarely-used control in a rail, not a place to spend
 *    a custom widget's accessibility budget. Each option carries its own role, which is fine inside a
 *    control: it is a description of the choice, not standing chrome.
 *  - **Unavailable** — a quiet notice, because `GET /v1/accounts` failing is not the same as having one
 *    Account, and silence would make the two indistinguishable.
 *
 * ## What this used to also be, and no longer is
 *
 * It carried the Account ID as a standing label and the role as a chip that doubled as the link to the
 * profile page (Req 3.1). Both are gone from the rail: the panel in the top bar states the Account ID and
 * the role, and holds "Manage Profile", so Requirement 3.1's entry point is still in the header banner —
 * one disclosure in front of it instead of a chip beside the nav. Three surfaces naming the same two
 * values had become the problem the rail was supposed to solve.
 *
 * ## Why the rail's divider is in here rather than in {@link import('./Layout').Layout}
 *
 * Because this component decides whether the rail has anything below the nav, and a divider is a claim
 * that it does. Layout used to wrap the switcher in `border-t border-hairline pt-4`; with the
 * single-Account branch returning `null` that wrapper would draw a rule under the nav with nothing
 * beneath it. The alternative — Layout asking "will the switcher render?" — means the same predicate
 * (`accounts.length <= 1 && error === undefined`) living in two files that have to keep agreeing, and it
 * would drift the first time a fourth state is added here. Owning the divider means `null` is the whole
 * answer: no switcher, no section, no rule. The cost is three utility classes in a file that otherwise
 * styles by class name, which is the smaller price.
 *
 * ## Switching navigates to the Checks list, and that is not laziness
 *
 * Every detail route carries an id scoped to one Account: `/checks/:checkId`, `/check-drafts/:draftId`,
 * `/results/:resultId`, `/traces/:traceId`. Those ids do not exist in the Account being switched to, so
 * staying put would replace the page with a `404` immediately after a successful switch — the switcher
 * would look broken at the exact moment it worked. Navigating to a route that is meaningful in every
 * Account is the honest outcome. It also discards page-local form state that belonged to the old
 * Account, which staying put would silently carry across.
 */

import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSelectedAccount } from '../account-context';

/**
 * The rail section this component occupies when it occupies one: a hairline above its content, rather
 * than a heading, because it is standing context for everything in the rail and not another destination
 * in the nav's list.
 *
 * Rendered by each branch that has something to show, and by none that does not — which is the whole
 * reason it lives here. See the module doc.
 */
function RailSection({ children }: { children: ReactNode }): JSX.Element {
  return <div className="border-t border-hairline pt-4">{children}</div>;
}

export function AccountSwitcher(): JSX.Element | null {
  const { accountId, accounts, loading, error, selectAccount } = useSelectedAccount();
  const navigate = useNavigate();

  function onChange(nextAccountId: string): void {
    if (nextAccountId === accountId) return;
    selectAccount(nextAccountId);
    // See the module doc: detail-route ids do not survive a switch.
    navigate('/');
  }

  // Nothing to switch between: the common case, and the one where a control would only add noise.
  if (accounts.length <= 1) {
    // A failed fetch is checked FIRST, and it is not the empty case. With no list the dashboard still
    // works against the home Account, so this is a degraded switcher rather than a broken dashboard — but
    // an unexplained absence would read as "you have one Account" instead of "we could not ask". The
    // error path is the one thing in this branch that must survive the branch rendering nothing.
    if (error !== undefined) {
      return (
        <RailSection>
          <span className="app-account-error" role="status">
            Accounts unavailable
          </span>
        </RailSection>
      );
    }
    // Includes the still-loading case, which used to show a "Loading…" label so the header never went
    // blank. There is nothing for it to label any more, and no blankness to avoid: for the
    // single-Account majority the rail simply ends at the nav, so a placeholder would appear under a
    // hairline and then take both away again. The multi-Account minority gets one appearance of the
    // `<select>` when the list lands, which is the control arriving rather than the rail flickering.
    return null;
  }

  return (
    <RailSection>
      <label className="app-account-switcher">
        <span className="app-account-switcher-label">Account</span>
        <select
          value={accountId ?? ''}
          disabled={loading}
          onChange={(event) => onChange(event.target.value)}
          aria-label="Acting as account"
        >
          {accounts.map((account) => (
            <option key={account.accountId} value={account.accountId}>
              {account.accountId} ({account.role})
            </option>
          ))}
        </select>
      </label>
    </RailSection>
  );
}
